import React, { useEffect, useRef, useState } from 'react';

/**
 * ScrollReveal component to animate elements as they enter the viewport.
 * Uses IntersectionObserver for high performance.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The content to animate
 * @param {string} props.className - Additional CSS classes
 * @param {number} props.delay - Delay in milliseconds before animation starts
 * @param {string} props.direction - Direction of slide: 'up', 'down', 'left', 'right', or 'none'
 */
export default function ScrollReveal({ 
  children, 
  className = '', 
  delay = 0, 
  direction = 'up' 
}) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target); // Animate only once
        }
      },
      {
        threshold: 0.05, // Trigger when 5% of the element is visible
        rootMargin: '0px 0px -40px 0px', // Trigger slightly before entering fully
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  // Determine starting transform based on direction
  const getDirectionClass = () => {
    if (!isVisible) {
      switch (direction) {
        case 'up': return 'opacity-0 translate-y-8';
        case 'down': return 'opacity-0 -translate-y-8';
        case 'left': return 'opacity-0 translate-x-8';
        case 'right': return 'opacity-0 -translate-x-8';
        case 'none': return 'opacity-0';
        default: return 'opacity-0 translate-y-8';
      }
    }
    return 'opacity-100 translate-y-0 translate-x-0';
  };

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${getDirectionClass()} ${className}`}
      style={{ 
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}
